import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import moment from 'moment';
import { useFormik } from 'formik';
import { FaLocationArrow } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { editData } from '../../../redux/actions/api/EditData';
import Breadcrumb from '../../../components/breadcrumb/Breadcrumb';
import Loading from '../../../components/loading/Loading';
import {
  checkIfFilesAreCorrectType,
  checkIfFilesAreTooBig,
  checkValidationPhone,
  DATE_TIME_FORMAT,
} from '../../../utils/function';

export default function EditCourses() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categoryId, courseId } = useParams();
  const { accessToken } = useSelector((state) => state.auth);
  const course = useSelector((state) =>
    state?.api?.data?.courses?.find(
      (course) => course.id.toString() === courseId
    )
  );
  const { loading, error } = useSelector((state) => state.api);
  // ----------------------------------------------------------------------------------->
  useEffect(() => {
    error && toast.error(error);
  }, [dispatch, error, loading]);
  // ----------------------------------------------------------------------------------->
  const initialValues = {
    // initial Values input felids
    image: '',
    hours: course?.hours,
    startAt: moment(course?.start_at).format(DATE_TIME_FORMAT),
    fees: course?.fees,
    status: course?.status,
    title: {
      ar: course?.title_ar,
      en: course?.title_en,
    },
    description: {
      ar: course?.description_ar,
      en: course?.description_en,
    },
  };
  // ----------------------------------------------------------------------------------->
  const validationSchema = Yup.object({
    // validation and check input is not empty
    title: Yup.object().shape({
      ar: Yup.string()
        .required(`title ar required`)
        .min(3, `The title ar you should not be less than 3 characters`)
        .max(70, `The title ar you must not than 70 characters`),
      en: Yup.string()
        .required(`title en required`)
        .min(3, `The title en you should not be less than 3 characters`)
        .max(70, `The title en you must not than 70 characters`),
    }),
    description: Yup.object().shape({
      ar: Yup.string()
        .required(`description ar required`)
        .min(3, `The description ar you should not be less than 3 characters`)
        .max(500, `The description ar you must not than 500 characters`),
      en: Yup.string()
        .required(`description en required`)
        .min(3, `The description en you should not be less than 3 characters`)
        .max(500, `The description en you must not than 500 characters`),
    }),
    image: Yup.mixed()
      .test(
        'fileSize',
        'File size too large, max file size is 2 Mb',
        (value) => !value || checkIfFilesAreTooBig(value)
      )
      .test(
        'fileFormat',
        'Uploaded file has unsupported format.',
        (value) => !value || checkIfFilesAreCorrectType(value)
      ),
    hours: Yup.string().required(`hours required`),
    fees: Yup.string().required(`fees required`),
    status: Yup.string().required(`status required`),
    startAt: Yup.string().required(`start at required`),
  });
  // ----------------------------------------------------------------------------------->
  const onSubmit = (values) => {
    // handel to modify course
    var data = new FormData();
    data.append('image', values?.image || []);
    data.append('course_category_id', categoryId);
    data.append('title_en', values?.title?.en);
    data.append('title_ar', values?.title?.ar);
    data.append('hours', values?.hours);
    data.append('start_at', values?.startAt);
    data.append('fees', values?.fees);
    data.append('status', values?.status);
    data.append('description_en', values?.description?.en);
    data.append('description_ar', values?.description?.ar);
    data.append('_method', 'PUT');
    dispatch(
      editData(
        `courses/${courseId}`,
        accessToken,
        data,
        navigate,
        '/categories',
        true
      )
    );
  };
  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
  });
  return (
    <>
      <Loading isLoading={loading} />
      <Breadcrumb
        title='Modify a course'
        textActive='Modify Course'
        items={[{ title: 'Categories' }, { title: 'Course' }]}
      />
      <div className='card rounded-1 mb-4 p-3'>
        <form onSubmit={formik.handleSubmit}>
          <div className='row'>
            <div className='col-md-6'>
              <div className='form-group'>
                <label htmlFor='title_ar' className='text-muted'>
                  Enter your title ar <span className='required'>*</span>
                </label>
                <input
                  className='form-control'
                  name='title.ar'
                  id='title_ar'
                  type='text'
                  inputMode='text'
                  {...formik.getFieldProps('title.ar')}
                  placeholder='write title here....'
                />
                {formik.touched.title?.ar && formik.errors.title?.ar ? (
                  <div className='text-error'>{formik.errors.title?.ar}</div>
                ) : null}
              </div>
            </div>
            <div className='col-md-6'>
              <div className='form-group'>
                <label htmlFor='title_en' className='text-muted'>
                  Enter your title en <span className='required'>*</span>
                </label>
                <input
                  className='form-control'
                  name='title.en'
                  id='title_en'
                  type='text'
                  inputMode='text'
                  placeholder='write title here....'
                  {...formik.getFieldProps('title.en')}
                />
                {formik.touched.title?.en && formik.errors.title?.en ? (
                  <div className='text-error'>{formik.errors.title?.en}</div>
                ) : null}
              </div>
            </div>
            <div className='col-md-6'>
              <div className='form-group'>
                <label htmlFor='hours' className='text-muted'>
                  Enter your hours <span className='required'>*</span>
                </label>
                <input
                  className='form-control'
                  name='hours'
                  id='hours'
                  type='number'
                  onInput={(e) => checkValidationPhone(e.target.value)}
                  {...formik.getFieldProps('hours')}
                />
                {formik.touched.hours && formik.errors.hours ? (
                  <div className='text-error'>{formik.errors.hours}</div>
                ) : null}
              </div>
            </div>
            <div className='col-md-6'>
              <div className='form-group'>
                <label htmlFor='startAt' className='text-muted'>
                  Enter your start at <span className='required'>*</span>
                </label>
                <input
                  className='form-control'
                  name='startAt'
                  id='startAt'
                  type='datetime-local'
                  {...formik.getFieldProps('startAt')}
                />
                {formik.touched.startAt && formik.errors.startAt ? (
                  <div className='text-error'>{formik.errors.startAt}</div>
                ) : null}
              </div>
            </div>
            <div className='col-md-6'>
              <div className='form-group'>
                <label htmlFor='fees' className='text-muted'>
                  Enter your fees <span className='required'>*</span>
                </label>
                <input
                  className='form-control'
                  name='fees'
                  id='fees'
                  onInput={(e) => checkValidationPhone(e.target.value)}
                  type='number'
                  {...formik.getFieldProps('fees')}
                />
                {formik.touched.fees && formik.errors.fees ? (
                  <div className='text-error'>{formik.errors.fees}</div>
                ) : null}
              </div>
            </div>
            <div className='col-md-6'>
              <div className='form-group'>
                <label htmlFor='status' className='text-muted'>
                  Select status
                </label>
                <select
                  className='form-select'
                  name='status'
                  id='status'
                  {...formik.getFieldProps('status')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value='' disabled>
                    Select Status
                  </option>
                  <option value='ongoing'>Ongoing</option>
                  <option value='upcoming'>Upcoming</option>
                </select>
                {formik.touched?.status && formik.errors?.status ? (
                  <div className='text-error'>{formik.errors?.status}</div>
                ) : null}
              </div>
            </div>
            <div className='col-md-6'>
              <div className='form-group'>
                <label htmlFor='description_ar' className='text-muted'>
                  Enter your description ar <span className='required'>*</span>
                </label>
                <textarea
                  className='form-control form-control-user'
                  name='description.ar'
                  inputMode='text'
                  {...formik.getFieldProps('description.ar')}
                  placeholder='write description here....'
                  id='description_ar'
                  cols='30'
                  rows='5'
                ></textarea>
                {formik.touched.description?.ar &&
                formik.errors.description?.ar ? (
                  <div className='text-error'>
                    {formik.errors.description?.ar}
                  </div>
                ) : null}
              </div>
            </div>
            <div className='col-md-6'>
              <div className='form-group'>
                <label htmlFor='description_en' className='text-muted'>
                  Enter your description en <span className='required'>*</span>
                </label>
                <textarea
                  className='form-control form-control-user'
                  name='description.en'
                  inputMode='text'
                  placeholder='write description here....'
                  id='description_en'
                  {...formik.getFieldProps('description.en')}
                  cols='30'
                  rows='5'
                ></textarea>
                {formik.touched.description?.en &&
                formik.errors.description?.en ? (
                  <div className='text-error'>
                    {formik.errors.description?.en}
                  </div>
                ) : null}
              </div>
            </div>
            <div className='col-md-6'>
              <div className='form-group'>
                <label htmlFor='image' className='text-muted'>
                  Enter your image <span className='required'>*</span>
                </label>
                <input
                  className='form-control'
                  name='image'
                  accept='image/jpeg,image/png,image/svg'
                  id='image'
                  onChange={(e) =>
                    formik.setFieldValue('image', e.target.files[0])
                  }
                  type='file'
                />
                {formik.touched.image && formik.errors.image ? (
                  <div className='text-error'>{formik.errors.image}</div>
                ) : null}
              </div>
            </div>
            <div className='col-md-12 mt-3 text-center'>
              <button
                className='btn btn-primary btn-user btn-inline'
                type='submit'
              >
                <FaLocationArrow style={{ marginRight: '.5rem' }} /> Edit
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
